import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  VIZ,
  MAX_BAR_THICKNESS,
  axisStyle,
  tooltipStyle,
  formatNumber,
  seriesColor,
  legendStyle,
} from '../chartTheme';

/**
 * The column total, drawn once above each stack. Segment values live in the
 * tooltip and the table view - printing every segment would bury the bars.
 */
const stackTotalPlugin = (formatter) => ({
  id: 'stackTotal',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const visible = chart.data.datasets
      .map((dataset, index) => ({ dataset, meta: chart.getDatasetMeta(index) }))
      .filter(({ meta }) => !meta.hidden);
    if (!visible.length) return;

    ctx.save();
    ctx.font = `600 11px 'Inter', system-ui, sans-serif`;
    ctx.fillStyle = VIZ.textSecondary;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    (chart.data.labels || []).forEach((_, index) => {
      const total = visible.reduce((sum, { dataset }) => sum + (Number(dataset.data[index]) || 0), 0);
      if (!total) return;
      const top = visible.reduce((min, { meta }) => {
        const bar = meta.data[index];
        return bar && bar.y < min ? bar.y : min;
      }, Infinity);
      const x = visible[0].meta.data[index]?.x;
      if (x === undefined || top === Infinity) return;
      ctx.fillText(formatter(total), x, top - 6);
    });
    ctx.restore();
  },
});

/**
 * Several series per period, as stacked columns (fees split by payment mode,
 * new vs follow-up per month...). Handles any number of datasets; each gets a
 * stable hue and a legend entry. `stacked={false}` draws grouped columns.
 */
export default function StackedColumnChart({ labels, datasets, valueFormatter = formatNumber, stacked = true }) {
  return (
    <Bar
      data={{
        labels,
        datasets: datasets.map((set, index) => ({
          label: set.label,
          data: set.data,
          backgroundColor: seriesColor(index, set.color),
          borderColor: VIZ.surface,
          borderWidth: stacked ? { top: 1, bottom: 0, left: 0, right: 0 } : 0,
          maxBarThickness: MAX_BAR_THICKNESS * (stacked ? 1.5 : 1),
          borderRadius: stacked ? 2 : 4,
          borderSkipped: stacked ? false : 'start',
          ...(stacked ? { stack: 'total' } : {}),
        })),
      }}
      plugins={stacked ? [stackTotalPlugin(valueFormatter)] : []}
      options={{
        maintainAspectRatio: false,
        layout: { padding: { top: 20 } },
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: { ...axisStyle(false), stacked },
          y: {
            ...axisStyle(true),
            stacked,
            beginAtZero: true,
            ticks: { ...axisStyle().ticks, maxTicksLimit: 5 },
          },
        },
        plugins: {
          legend: legendStyle(),
          tooltip: tooltipStyle(valueFormatter),
        },
      }}
    />
  );
}
